// R-04: bước thời gian chuẩn (median các khoảng dương liên tiếp) để ngoại suy
// ngoài biên không bị phóng đại bởi kỳ nghỉ/cuối tuần (vd 64h qua weekend).
function medianPositiveStep(timestamps) {
    const steps = [];
    for (let i = 1; i < timestamps.length; i += 1) {
        const step = timestamps[i] - timestamps[i - 1];
        if (Number.isFinite(step) && step > 0) {
            steps.push(step);
        }
    }
    if (steps.length === 0) {
        return null;
    }
    steps.sort((a, b) => a - b);
    return steps[Math.floor(steps.length / 2)];
}
function resolveInterpolatedIndexByTime(timestamp, timestamps, referenceStepMs) {
    if (timestamps.length === 0 || !Number.isFinite(timestamp)) {
        return null;
    }
    if (timestamps.length === 1) {
        return 0;
    }
    const firstTime = timestamps[0];
    const lastTime = timestamps[timestamps.length - 1];
    if (timestamp <= firstTime) {
        const secondTime = timestamps[1];
        if (!Number.isFinite(secondTime) || secondTime === firstTime) {
            return 0;
        }
        return (timestamp - firstTime) / (referenceStepMs ?? (secondTime - firstTime));
    }
    if (timestamp >= lastTime) {
        const penultimateTime = timestamps[timestamps.length - 2];
        if (!Number.isFinite(penultimateTime) || lastTime === penultimateTime) {
            return timestamps.length - 1;
        }
        return (timestamps.length - 1) + ((timestamp - lastTime) / (referenceStepMs ?? (lastTime - penultimateTime)));
    }
    let low = 0;
    let high = timestamps.length - 1;
    while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const midTime = timestamps[mid];
        if (midTime === timestamp) {
            return mid;
        }
        if (midTime < timestamp) {
            low = mid + 1;
        }
        else {
            high = mid - 1;
        }
    }
    const rightIndex = Math.min(timestamps.length - 1, Math.max(1, low));
    const leftIndex = rightIndex - 1;
    const leftTime = timestamps[leftIndex];
    const rightTime = timestamps[rightIndex];
    if (rightTime <= leftTime) {
        return leftIndex;
    }
    const ratio = (timestamp - leftTime) / (rightTime - leftTime);
    return leftIndex + ratio;
}
function resolveInterpolatedTimeByIndex(index, timestamps, referenceStepMs) {
    if (timestamps.length === 0 || !Number.isFinite(index)) {
        return null;
    }
    if (timestamps.length === 1) {
        return timestamps[0];
    }
    if (index <= 0) {
        const firstTime = timestamps[0];
        const secondTime = timestamps[1];
        if (!Number.isFinite(firstTime) || !Number.isFinite(secondTime)) {
            return null;
        }
        return firstTime + index * (referenceStepMs ?? (secondTime - firstTime));
    }
    if (index >= timestamps.length - 1) {
        const lastTime = timestamps[timestamps.length - 1];
        const penultimateTime = timestamps[timestamps.length - 2];
        if (!Number.isFinite(lastTime) || !Number.isFinite(penultimateTime)) {
            return null;
        }
        return lastTime + (index - (timestamps.length - 1)) * (referenceStepMs ?? (lastTime - penultimateTime));
    }
    const leftIndex = Math.floor(index);
    const rightIndex = Math.ceil(index);
    const leftTime = timestamps[leftIndex];
    const rightTime = timestamps[rightIndex];
    if (!Number.isFinite(leftTime) || !Number.isFinite(rightTime)) {
        return null;
    }
    if (leftIndex === rightIndex || rightTime <= leftTime) {
        return leftTime;
    }
    const ratio = index - leftIndex;
    return leftTime + ratio * (rightTime - leftTime);
}
export function createTimeIndexMap(data, resolveTimestamp) {
    const timestamps = data.map((item) => resolveTimestamp(item));
    // R-07: tính 1 lần khi khởi tạo thay vì mỗi lần tra cứu ngoài biên.
    const referenceStepMs = medianPositiveStep(timestamps);
    const bounds = timestamps.length > 0
        ? { firstTime: timestamps[0], lastTime: timestamps[timestamps.length - 1], length: timestamps.length }
        : null;
    return {
        resolveIndexByTime: (timestamp) => resolveInterpolatedIndexByTime(timestamp, timestamps, referenceStepMs),
        resolveTimeByIndex: (index) => resolveInterpolatedTimeByIndex(index, timestamps, referenceStepMs),
        getTimeBounds: () => bounds,
    };
}
export function createDateTimeIndexMap(data) {
    return createTimeIndexMap(data, (item) => item.date.getTime());
}
// R-07: cache map theo reference của mảng dữ liệu — đường vẽ nóng gọi mỗi
// frame nhưng plotData không đổi thì tái dùng map (0 tái cấp phát mảng times).
// WeakMap để mảng cũ được GC khi dữ liệu thay reference.
const dateTimeIndexMapCache = new WeakMap();
export function getCachedDateTimeIndexMap(data) {
    const cached = dateTimeIndexMapCache.get(data);
    if (cached) {
        return cached;
    }
    const created = createDateTimeIndexMap(data);
    dateTimeIndexMapCache.set(data, created);
    return created;
}
